import {ActionIcon, Box, Button, Flex, Table, Text} from '@mantine/core'
import {type Time, TimeRange, Workday} from '@package/shared'
import {IconX} from '@tabler/icons-react'
import {CustomTimePicker} from '../../../../components/customTimePicker/CustomTimePicker.tsx'
import {useForm} from '@mantine/form'
import {zod4Resolver} from 'mantine-form-zod-resolver'
import {z} from 'zod'

type Props = {
  weekday: string
  defaultValue: Workday
  onValueChange: (workday: Workday) => void
  disabled: boolean
}

export const FormValues = z.object({timeRanges: Workday})
export type FormValues = z.infer<typeof FormValues>

const defaultWorkday = TimeRange.parse({start: '09:00', end: '17:00'})
export const WorkdayFormRows = ({weekday, defaultValue, onValueChange, disabled: containerDisabled}: Props) => {
  const form = useForm<FormValues>({
    initialValues: {timeRanges: defaultValue},
    validate: zod4Resolver(FormValues),
    validateInputOnChange: true,
    initialErrors: zod4Resolver(FormValues)({timeRanges: defaultValue}),
    onValuesChange: (values) => {
      form.validate()
      const {success, data} = Workday.safeParse(values.timeRanges)
      if (success) onValueChange(data)
    },
  })

  const disabled = containerDisabled

  return (
    <Table.Tr>
      <Table.Th w={130} style={{verticalAlign: 'top'}}>
        <Text>{weekday}</Text>
      </Table.Th>
      <Table.Td>
        <Flex direction={'column'} gap={10}>
          {form.values.timeRanges.map((_, index) => {
            const rootPath = `timeRanges.${index}`
            const startPath = `timeRanges.${index}.start`
            const endPath = `timeRanges.${index}.end`

            return (
              <Flex key={form.key(rootPath)} gap={10}>
                <CustomTimePicker
                  key={form.key(startPath)}
                  {...form.getInputProps(startPath)}
                  value={form.values.timeRanges[index].start}
                  onChange={(value) => value && form.setFieldValue(startPath, value)}
                  style={{flex: 1}}
                  disabled={disabled}
                />
                <CustomTimePicker
                  key={form.key(endPath)}
                  {...form.getInputProps(endPath)}
                  value={form.values.timeRanges[index].end}
                  onChange={(value) => value && form.setFieldValue(endPath, value)}
                  style={{flex: 1}}
                  disabled={disabled}
                />
                <ActionIcon
                  mt={1}
                  title={'Delete'}
                  color={'red'}
                  variant={'light'}
                  size={'lg'}
                  onClick={() => form.removeListItem('timeRanges', index)}
                  disabled={disabled || !defaultValue}
                >
                  <IconX stroke={1} />
                </ActionIcon>
              </Flex>
            )
          })}
          <Box pr={45}>
            <Button
              variant={'default'}
              onClick={() => {
                const lastEnd = form.values.timeRanges.at(-1)?.end.split(':')
                form.insertListItem('timeRanges', {
                  ...(lastEnd
                    ? {
                        start: lastEnd.join(':') as Time,
                        end: [(+lastEnd[0] + 1).toString().padStart(2, '0'), lastEnd[1]].join(':') as Time,
                      }
                    : defaultWorkday),
                })
              }}
              disabled={disabled}
            >
              Add working hours
            </Button>
          </Box>
        </Flex>
      </Table.Td>
    </Table.Tr>
  )
}
